import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../shared/navbar';
import { API_BASE_URL } from '../../config';
import ApplicationQuestionsManager from '../applicant/ApplicationQuestionsManager';
import EmailTemplatesManager from '../crm/EmailTemplatesManager';
import './AdminDashboard.css';

// ─── Helpers ─────────────────────────────────────────────
const initials = (first, last) => {
  const f = (first || '').charAt(0).toUpperCase();
  const l = (last || '').charAt(0).toUpperCase();
  return f + l || '?';
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const rolePretty = (r) => {
  if (!r) return 'No Role';
  return r.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// ─── KPI Card ────────────────────────────────────────────
function KpiCard({ label, value, sub, icon, color }) {
  return (
    <div className="adm-kpi" style={{ borderTopColor: color }}>
      <div className="adm-kpi-icon" style={{ background: color + '18', color }}>{icon}</div>
      <div className="adm-kpi-info">
        <span className="adm-kpi-value">{value}</span>
        <span className="adm-kpi-label">{label}</span>
        {sub && <span className="adm-kpi-sub">{sub}</span>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState({ employees: [], tenants: [], applicants: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissionOptions, setPermissionOptions] = useState({ tabs: [], pages: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [toast, setToast] = useState(null);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const fetchUsers = useCallback(async () => {
    try {
      const username = localStorage.getItem('username');
      const res = await fetch(`${API_BASE_URL}/api/admin/users?username=${username}`);
      if (res.ok) setUsers(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/stats`);
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/roles`);
      if (res.ok) { const d = await res.json(); setRoles(d.roles); }
    } catch (e) { console.error(e); }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/permissions/options`);
      if (res.ok) setPermissionOptions(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchStats();
    fetchRoles();
    fetchPermissions();
  }, [fetchUsers, fetchStats, fetchRoles, fetchPermissions]);

  // ── Save handlers ──────────────────────────────────────
  const handleSaveEmployee = async () => {
    try {
      const username = localStorage.getItem('username');
      const isNew = !editingUser.id;
      const url = isNew
        ? `${API_BASE_URL}/api/admin/users`
        : `${API_BASE_URL}/api/admin/users/${editingUser.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...editingUser, requesting_username: username })
      });

      if (res.ok) {
        setToast({ type: 'success', msg: isNew ? 'Employee created!' : 'Employee updated!' });
        setEditingUser(null);
        fetchUsers();
        fetchStats();
      } else {
        const d = await res.json();
        setToast({ type: 'error', msg: d.error || 'Failed to save' });
      }
    } catch (e) {
      setToast({ type: 'error', msg: 'Network error' });
    }
  };

  const handleSaveTenant = async () => {
    try {
      const username = localStorage.getItem('username');
      const res = await fetch(`${API_BASE_URL}/api/admin/tenants/${editingUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...editingUser, requesting_username: username })
      });
      if (res.ok) {
        setToast({ type: 'success', msg: 'Tenant updated!' });
        setEditingUser(null);
        fetchUsers();
      } else {
        const d = await res.json();
        setToast({ type: 'error', msg: d.error || 'Failed' });
      }
    } catch (e) { setToast({ type: 'error', msg: 'Network error' }); }
  };

  const handleSaveApplicant = async () => {
    try {
      const username = localStorage.getItem('username');
      const res = await fetch(`${API_BASE_URL}/api/admin/applicants/${editingUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...editingUser, requesting_username: username })
      });
      if (res.ok) {
        setToast({ type: 'success', msg: 'Applicant updated!' });
        setEditingUser(null);
        fetchUsers();
      } else {
        const d = await res.json();
        setToast({ type: 'error', msg: d.error || 'Failed' });
      }
    } catch (e) { setToast({ type: 'error', msg: 'Network error' }); }
  };

  const handleToggleActive = async (emp) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${emp.id}/toggle-active`, { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        setToast({ type: 'success', msg: d.message });
        fetchUsers();
        fetchStats();
      }
    } catch (e) { setToast({ type: 'error', msg: 'Failed to toggle' }); }
  };

  const togglePermission = (type, item) => {
    const current = editingUser.permissions?.[type] || [];
    const updated = current.includes(item)
      ? current.filter(p => p !== item)
      : [...current, item];
    setEditingUser({
      ...editingUser,
      permissions: { ...editingUser.permissions, [type]: updated }
    });
  };

  // ── Filter helpers ─────────────────────────────────────
  const filteredEmployees = (users.employees || []).filter(emp => {
    const s = searchTerm.toLowerCase();
    const match = !s ||
      emp.username?.toLowerCase().includes(s) ||
      emp.first_name?.toLowerCase().includes(s) ||
      emp.last_name?.toLowerCase().includes(s) ||
      emp.email?.toLowerCase().includes(s);
    const roleMatch = filterRole === 'all' || emp.employee_role === filterRole;
    return match && roleMatch;
  });

  const filteredTenants = (users.tenants || []).filter(t => {
    const s = searchTerm.toLowerCase();
    return !s ||
      t.first_name?.toLowerCase().includes(s) ||
      t.last_name?.toLowerCase().includes(s) ||
      t.email?.toLowerCase().includes(s) ||
      t.tenant_code?.toLowerCase().includes(s);
  });

  const filteredApplicants = (users.applicants || []).filter(a => {
    const s = searchTerm.toLowerCase();
    return !s ||
      a.first_name?.toLowerCase().includes(s) ||
      a.last_name?.toLowerCase().includes(s) ||
      a.email?.toLowerCase().includes(s);
  });

  // ── Tabs config ────────────────────────────────────────
  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'employees', label: `Employees (${(users.employees || []).length})` },
    { key: 'tenants', label: `Tenants (${(users.tenants || []).length})` },
    { key: 'applicants', label: `Applicants (${(users.applicants || []).length})` },
    { key: 'questions', label: 'Application Questions' },
    { key: 'templates', label: 'Email Templates' },
  ];

  if (loading) {
    return (
      <div className="adm-page">
        <Navbar selected="Admin" />
        <div className="adm-loading"><div className="adm-spinner" />Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="adm-page">
      <Navbar selected="Admin" />

      {/* Toast */}
      {toast && (
        <div className={`adm-toast adm-toast-${toast.type}`} onClick={() => setToast(null)}>
          <span>{toast.type === 'success' ? '\u2713' : '\u26A0'}</span>
          <span>{toast.msg}</span>
        </div>
      )}

      <div className="adm-container">
        {/* Header */}
        <div className="adm-header">
          <div>
            <h1 className="adm-title">Admin Dashboard</h1>
            <p className="adm-subtitle">Manage users, permissions, and system configuration</p>
          </div>
          {activeTab === 'employees' && (
            <button className="adm-primary-btn" onClick={() => setEditingUser({
              account_type: 'employee',
              permissions: { tabs: [], pages: [] },
              username: '', password: '', first_name: '', last_name: '',
              email: '', phone: '', employee_role: '', is_active: 1
            })}>
              + New Employee
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="adm-tabs">
          {tabs.map(t => (
            <button key={t.key} className={`adm-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => { setActiveTab(t.key); setSearchTerm(''); setFilterRole('all'); }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ═══ OVERVIEW TAB ═══ */}
        {activeTab === 'overview' && stats && (
          <div className="adm-overview">
            <div className="adm-kpis">
              <KpiCard label="Employees" value={stats.total_employees} sub={`${stats.active_employees} active`} icon="👥" color="#6366f1" />
              <KpiCard label="Tenants" value={stats.total_tenants} icon="🏠" color="#10b981" />
              <KpiCard label="Applicants" value={stats.total_applicants} icon="📋" color="#f59e0b" />
              <KpiCard label="Properties" value={stats.total_properties} sub={`${stats.total_units} units`} icon="🏢" color="#3b82f6" />
            </div>

            {/* Applicant status breakdown */}
            {stats.applicant_status && Object.keys(stats.applicant_status).length > 0 && (
              <div className="adm-card">
                <h3 className="adm-card-title">Applicant Pipeline</h3>
                <div className="adm-pipeline">
                  {Object.entries(stats.applicant_status).map(([status, count]) => (
                    <div key={status} className="adm-pipeline-item">
                      <span className={`adm-status-dot s-${status}`} />
                      <span className="adm-pipeline-label">{rolePretty(status)}</span>
                      <span className="adm-pipeline-count">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent activity */}
            <div className="adm-card">
              <h3 className="adm-card-title">Recent Employees</h3>
              {stats.recent_employees && stats.recent_employees.length > 0 ? (
                <div className="adm-activity-list">
                  {stats.recent_employees.map(u => (
                    <div key={u.id} className="adm-activity-row">
                      <div className={`adm-avatar ${u.is_active === 0 ? 'inactive' : ''}`}>{initials(u.first_name, u.last_name)}</div>
                      <div className="adm-activity-info">
                        <span className="adm-activity-name">{u.first_name || ''} {u.last_name || ''}</span>
                        <span className="adm-activity-meta">{u.username} &middot; {rolePretty(u.employee_role)}</span>
                      </div>
                      <span className="adm-activity-date">{fmtDate(u.created_at)}</span>
                      <span className={`adm-active-chip ${u.is_active === 0 ? 'off' : 'on'}`}>{u.is_active === 0 ? 'Inactive' : 'Active'}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="adm-empty-text">No employees yet</p>}
            </div>

            <div className="adm-card">
              <h3 className="adm-card-title">Recent Applicants</h3>
              {stats.recent_applicants && stats.recent_applicants.length > 0 ? (
                <div className="adm-activity-list">
                  {stats.recent_applicants.map(a => (
                    <div key={a.id} className="adm-activity-row">
                      <div className="adm-avatar">{initials(a.first_name, a.last_name)}</div>
                      <div className="adm-activity-info">
                        <span className="adm-activity-name">{a.first_name || ''} {a.last_name || ''}</span>
                        <span className="adm-activity-meta">{a.email}</span>
                      </div>
                      <span className="adm-activity-date">{fmtDate(a.created_at)}</span>
                      <span className={`adm-status-badge s-${a.status}`}>{a.status || 'new'}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="adm-empty-text">No applicants yet</p>}
            </div>
          </div>
        )}

        {/* ═══ EMPLOYEES TAB ═══ */}
        {activeTab === 'employees' && (
          <div className="adm-tab-content">
            <div className="adm-toolbar">
              <input className="adm-search" type="text" placeholder="Search employees..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              <select className="adm-select" value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                <option value="all">All Roles</option>
                {roles.map(r => <option key={r} value={r}>{rolePretty(r)}</option>)}
              </select>
            </div>

            {filteredEmployees.length === 0 ? (
              <div className="adm-empty-state">
                <div className="adm-empty-icon">👥</div>
                <p>No employees found{searchTerm ? '. Try adjusting your search.' : '.'}</p>
              </div>
            ) : (
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Username</th>
                      <th>Role</th>
                      <th>Permissions</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map(emp => (
                      <tr key={emp.id} className={emp.is_active === 0 ? 'row-inactive' : ''}>
                        <td>
                          <div className="adm-user-cell">
                            <div className={`adm-avatar sm ${emp.is_active === 0 ? 'inactive' : ''}`}>{initials(emp.first_name, emp.last_name)}</div>
                            <div>
                              <div className="adm-user-name">{emp.first_name || ''} {emp.last_name || ''}</div>
                              <div className="adm-user-email">{emp.email || '—'}</div>
                            </div>
                          </div>
                        </td>
                        <td><code className="adm-code">{emp.username}</code></td>
                        <td><span className="adm-role-badge">{rolePretty(emp.employee_role)}</span></td>
                        <td>
                          <span className="adm-perm-count">{emp.permissions?.tabs?.length || 0} tabs, {emp.permissions?.pages?.length || 0} pages</span>
                        </td>
                        <td>
                          <button className={`adm-active-toggle ${emp.is_active === 0 ? 'off' : 'on'}`} onClick={() => handleToggleActive(emp)} title={emp.is_active === 0 ? 'Click to activate' : 'Click to deactivate'}>
                            <span className="adm-toggle-dot" />
                            <span>{emp.is_active === 0 ? 'Inactive' : 'Active'}</span>
                          </button>
                        </td>
                        <td className="adm-date-cell">{fmtDate(emp.created_at)}</td>
                        <td>
                          <button className="adm-action-btn" onClick={() => setEditingUser({ ...emp, account_type: 'employee', permissions: emp.permissions || { tabs: [], pages: [] } })}>Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ═══ TENANTS TAB ═══ */}
        {activeTab === 'tenants' && (
          <div className="adm-tab-content">
            <div className="adm-toolbar">
              <input className="adm-search" type="text" placeholder="Search tenants..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>

            {filteredTenants.length === 0 ? (
              <div className="adm-empty-state">
                <div className="adm-empty-icon">🏠</div>
                <p>No tenants found.</p>
              </div>
            ) : (
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Code</th>
                      <th>Monthly Rent</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTenants.map(t => (
                      <tr key={t.id}>
                        <td>
                          <div className="adm-user-cell">
                            <div className="adm-avatar sm tenant">{initials(t.first_name, t.last_name)}</div>
                            <span className="adm-user-name">{t.first_name} {t.last_name}</span>
                          </div>
                        </td>
                        <td>{t.email || '—'}</td>
                        <td>{t.phone || '—'}</td>
                        <td><code className="adm-code">{t.tenant_code || '—'}</code></td>
                        <td className="adm-money">${(t.monthly_rent || 0).toLocaleString()}</td>
                        <td>
                          <button className="adm-action-btn" onClick={() => setEditingUser({ ...t, account_type: 'tenant' })}>Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ═══ APPLICANTS TAB ═══ */}
        {activeTab === 'applicants' && (
          <div className="adm-tab-content">
            <div className="adm-toolbar">
              <input className="adm-search" type="text" placeholder="Search applicants..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>

            {filteredApplicants.length === 0 ? (
              <div className="adm-empty-state">
                <div className="adm-empty-icon">📋</div>
                <p>No applicants found.</p>
              </div>
            ) : (
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Applicant</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Property Interest</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplicants.map(a => (
                      <tr key={a.id}>
                        <td>
                          <div className="adm-user-cell">
                            <div className="adm-avatar sm applicant">{initials(a.first_name, a.last_name)}</div>
                            <span className="adm-user-name">{a.first_name} {a.last_name}</span>
                          </div>
                        </td>
                        <td>{a.email || '—'}</td>
                        <td>{a.phone || '—'}</td>
                        <td><span className={`adm-status-badge s-${a.status}`}>{a.status || 'new'}</span></td>
                        <td>{a.property_interest || '—'}</td>
                        <td>
                          <button className="adm-action-btn" onClick={() => setEditingUser({ ...a, account_type: 'applicant' })}>Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ═══ APPLICATION QUESTIONS TAB ═══ */}
        {activeTab === 'questions' && <ApplicationQuestionsManager />}

        {/* ═══ EMAIL TEMPLATES TAB ═══ */}
        {activeTab === 'templates' && <EmailTemplatesManager />}

        {/* ═══ EDIT MODAL ═══ */}
        {editingUser && (
          <div className="adm-modal-overlay" onClick={() => setEditingUser(null)}>
            <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="adm-modal-header">
                <h2>{!editingUser.id ? 'Create' : 'Edit'} {editingUser.account_type === 'tenant' ? 'Tenant' : editingUser.account_type === 'applicant' ? 'Applicant' : 'Employee'}</h2>
                <button className="adm-modal-close" onClick={() => setEditingUser(null)}>x</button>
              </div>
              <div className="adm-modal-body">
                {/* ── EMPLOYEE FORM ── */}
                {editingUser.account_type === 'employee' && (
                  <>
                    <div className="adm-form-row">
                      <div className="adm-field">
                        <label>Username {!editingUser.id && <span className="req">*</span>}</label>
                        <input type="text" value={editingUser.username || ''} onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })} disabled={!!editingUser.id} />
                      </div>
                      <div className="adm-field">
                        <label>{editingUser.id ? 'Reset Password' : 'Password'} {!editingUser.id && <span className="req">*</span>}</label>
                        <input type="password" value={editingUser.password || ''} onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })} placeholder={editingUser.id ? 'Leave blank to keep current' : 'Enter password'} />
                      </div>
                    </div>
                    <div className="adm-form-row">
                      <div className="adm-field">
                        <label>First Name</label>
                        <input type="text" value={editingUser.first_name || ''} onChange={(e) => setEditingUser({ ...editingUser, first_name: e.target.value })} />
                      </div>
                      <div className="adm-field">
                        <label>Last Name</label>
                        <input type="text" value={editingUser.last_name || ''} onChange={(e) => setEditingUser({ ...editingUser, last_name: e.target.value })} />
                      </div>
                    </div>
                    <div className="adm-form-row">
                      <div className="adm-field">
                        <label>Email</label>
                        <input type="email" value={editingUser.email || ''} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} />
                      </div>
                      <div className="adm-field">
                        <label>Phone</label>
                        <input type="text" value={editingUser.phone || ''} onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })} />
                      </div>
                    </div>
                    <div className="adm-field">
                      <label>Employee Role</label>
                      <select value={editingUser.employee_role || ''} onChange={(e) => setEditingUser({ ...editingUser, employee_role: e.target.value })}>
                        <option value="">Select Role</option>
                        {roles.map(r => <option key={r} value={r}>{rolePretty(r)}</option>)}
                      </select>
                    </div>

                    <div className="adm-field">
                      <label>Menu Tabs</label>
                      <div className="adm-perm-grid">
                        {permissionOptions.tabs.map(tab => (
                          <label key={tab} className="adm-perm-item">
                            <input type="checkbox" checked={(editingUser.permissions?.tabs || []).includes(tab)} onChange={() => togglePermission('tabs', tab)} />
                            <span>{tab}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="adm-field">
                      <label>Page Access</label>
                      <div className="adm-perm-grid">
                        {permissionOptions.pages.map(page => (
                          <label key={page} className="adm-perm-item">
                            <input type="checkbox" checked={(editingUser.permissions?.pages || []).includes(page)} onChange={() => togglePermission('pages', page)} />
                            <span>{page}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* ── TENANT FORM ── */}
                {editingUser.account_type === 'tenant' && (
                  <>
                    <div className="adm-form-row">
                      <div className="adm-field">
                        <label>First Name</label>
                        <input type="text" value={editingUser.first_name || ''} onChange={(e) => setEditingUser({ ...editingUser, first_name: e.target.value })} />
                      </div>
                      <div className="adm-field">
                        <label>Last Name</label>
                        <input type="text" value={editingUser.last_name || ''} onChange={(e) => setEditingUser({ ...editingUser, last_name: e.target.value })} />
                      </div>
                    </div>
                    <div className="adm-form-row">
                      <div className="adm-field">
                        <label>Email</label>
                        <input type="email" value={editingUser.email || ''} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} />
                      </div>
                      <div className="adm-field">
                        <label>Phone</label>
                        <input type="text" value={editingUser.phone || ''} onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })} />
                      </div>
                    </div>
                    <div className="adm-form-row">
                      <div className="adm-field">
                        <label>Tenant Code</label>
                        <input type="text" value={editingUser.tenant_code || ''} onChange={(e) => setEditingUser({ ...editingUser, tenant_code: e.target.value })} />
                      </div>
                      <div className="adm-field">
                        <label>Monthly Rent</label>
                        <input type="number" value={editingUser.monthly_rent || ''} onChange={(e) => setEditingUser({ ...editingUser, monthly_rent: parseFloat(e.target.value) })} />
                      </div>
                    </div>
                  </>
                )}

                {/* ── APPLICANT FORM ── */}
                {editingUser.account_type === 'applicant' && (
                  <>
                    <div className="adm-form-row">
                      <div className="adm-field">
                        <label>First Name</label>
                        <input type="text" value={editingUser.first_name || ''} onChange={(e) => setEditingUser({ ...editingUser, first_name: e.target.value })} />
                      </div>
                      <div className="adm-field">
                        <label>Last Name</label>
                        <input type="text" value={editingUser.last_name || ''} onChange={(e) => setEditingUser({ ...editingUser, last_name: e.target.value })} />
                      </div>
                    </div>
                    <div className="adm-form-row">
                      <div className="adm-field">
                        <label>Email</label>
                        <input type="email" value={editingUser.email || ''} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} />
                      </div>
                      <div className="adm-field">
                        <label>Phone</label>
                        <input type="text" value={editingUser.phone || ''} onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })} />
                      </div>
                    </div>
                    <div className="adm-field">
                      <label>Status</label>
                      <select value={editingUser.status || 'new'} onChange={(e) => setEditingUser({ ...editingUser, status: e.target.value })}>
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="application">Application</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
              <div className="adm-modal-footer">
                <button className="adm-btn-cancel" onClick={() => setEditingUser(null)}>Cancel</button>
                <button
                  className="adm-btn-save"
                  onClick={() => {
                    if (editingUser.account_type === 'employee') handleSaveEmployee();
                    else if (editingUser.account_type === 'tenant') handleSaveTenant();
                    else handleSaveApplicant();
                  }}
                  disabled={!editingUser.id && editingUser.account_type === 'employee' && (!editingUser.username || !editingUser.password)}
                >
                  {!editingUser.id ? 'Create' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
